import { useEffect, useMemo, useState } from "react";
import {
  FiEdit2,
  FiGift,
  FiPlus,
  FiSearch,
  FiTag,
  FiTrash2,
} from "react-icons/fi";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";
import PromotionModal from "../../components/masters/PromotionModal";
import ClientPagination, { useClientPagination } from "../../components/common/ClientPagination";

const statusStyle = {
  ACTIVE: "bg-green-100 text-green-700",
  DRAFT: "bg-yellow-100 text-yellow-700",
  INACTIVE: "bg-gray-100 text-gray-700",
  ARCHIVED: "bg-red-100 text-red-700",
};

const formatDate = (value) => {
  if (!value) return "No schedule";

  return new Date(value).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export default function PromotionsPage() {
  const { accessToken } = useAuth();

  const [promotions, setPromotions] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [selectedPromotion, setSelectedPromotion] = useState(null);

  const loadPromotions = async () => {
    if (!accessToken) return;

    try {
      setLoading(true);
      setError("");

      const response = await apiRequest("/promotions", { accessToken });
      setPromotions(Array.isArray(response) ? response : []);
    } catch (requestError) {
      setError(requestError.message || "Unable to load promotions.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPromotions();
  }, [accessToken]);

  const filteredPromotions = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return promotions.filter((promotion) => {
      const couponCode = promotion.coupon?.code || "";

      const matchesSearch =
        !normalizedSearch ||
        promotion.name.toLowerCase().includes(normalizedSearch) ||
        couponCode.toLowerCase().includes(normalizedSearch) ||
        promotion.promoBarText?.toLowerCase().includes(normalizedSearch);

      const matchesStatus =
        statusFilter === "ALL" || promotion.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [promotions, search, statusFilter]);

  const activePromotions = promotions.filter(
    (promotion) => promotion.status === "ACTIVE",
  ).length;
  const promotionsPagination = useClientPagination(filteredPromotions);

  const visiblePromoBars = promotions.filter(
    (promotion) =>
      promotion.status === "ACTIVE" && promotion.showInPromoBar,
  ).length;

  const couponsCreated = promotions.filter((promotion) => promotion.coupon)
    .length;

  const handleSave = async (payload) => {
    setActionError("");

    try {
      if (selectedPromotion) {
        await apiRequest(`/promotions/${selectedPromotion.id}`, {
          method: "PATCH",
          body: payload,
          accessToken,
        });
      } else {
        await apiRequest("/promotions", {
          method: "POST",
          body: payload,
          accessToken,
        });
      }

      setShowModal(false);
      setSelectedPromotion(null);
      await loadPromotions();
    } catch (requestError) {
      setActionError(
        requestError.message || "Unable to save this promotion.",
      );
      throw requestError;
    }
  };

  const handleDelete = async (promotion) => {
    const confirmed = window.confirm(
      `Archive "${promotion.name}"? Its coupon will stop working and it will disappear from the promo bar.`,
    );

    if (!confirmed) return;

    try {
      setActionError("");

      await apiRequest(`/promotions/${promotion.id}`, {
        method: "DELETE",
        accessToken,
      });

      await loadPromotions();
    } catch (requestError) {
      setActionError(
        requestError.message || "Unable to archive this promotion.",
      );
    }
  };

  return (
    <div>
      <div className="mb-8 flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-sm text-gray-500">Marketing / Promotions</p>

          <h1 className="mt-1 text-3xl font-bold text-gray-900">
            Promotions & Coupons
          </h1>

          <p className="mt-2 text-gray-500">
            Manage coupon offers for POS and online checkout, with optional website banners.
          </p>
        </div>

        <button
          type="button"
          onClick={() => {
            setSelectedPromotion(null);
            setShowModal(true);
          }}
          className="flex h-11 items-center gap-2 rounded-xl bg-primary-700 px-5 font-medium text-white hover:bg-primary-800"
        >
          <FiPlus />
          Create Promotion
        </button>
      </div>

      <div className="mb-6 rounded-2xl border border-primary-100 bg-primary-50 p-5 text-sm text-primary-950">
        <p className="font-semibold">One coupon, POS and online</p>
        <p className="mt-2">Create a percentage or fixed-amount coupon, set the campaign to Active, and share its code. Customers enter it online; cashiers select a customer and apply it in POS. Minimum spend, discount cap, dates and usage limits are checked before the sale is saved.</p>
        <p className="mt-2">The website banner is optional and does not apply a discount automatically. Per-customer and total usage limits are shared across both channels. Reversed redemptions do not count.</p>
      </div>
      <div className="mb-8 grid gap-5 md:grid-cols-4">
        <StatCard label="Total Campaigns" value={promotions.length} />

        <StatCard
          label="Active Campaigns"
          value={activePromotions}
          valueClass="text-green-600"
        />

        <StatCard
          label="Visible Promo Bars"
          value={visiblePromoBars}
          valueClass="text-primary-700"
        />

        <StatCard
          label="Coupons Created"
          value={couponsCreated}
          valueClass="text-blue-600"
        />
      </div>

      <div className="overflow-hidden rounded-3xl border border-gray-100 bg-white">
        <div className="flex flex-col gap-4 border-b border-gray-100 p-5 md:flex-row md:items-center md:justify-between">
          <div className="relative w-full md:w-96">
            <FiSearch
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
              size={18}
            />

            <input
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search campaigns or coupon codes..."
              className="h-12 w-full rounded-xl border border-gray-200 pl-12 pr-4 outline-none focus:border-primary-700"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            className="h-12 rounded-xl border border-gray-200 bg-white px-4"
          >
            <option value="ALL">All Status</option>
            <option value="ACTIVE">Active</option>
            <option value="DRAFT">Draft</option>
            <option value="INACTIVE">Inactive</option>
            <option value="ARCHIVED">Archived</option>
          </select>
        </div>

        {(error || actionError) && (
          <div className="m-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error || actionError}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                {[
                  "Campaign",
                  "Coupon",
                  "Offer",
                  "Schedule",
                  "Promo Bar",
                  "Status",
                  "Actions",
                ].map((heading) => (
                  <th
                    key={heading}
                    className={`px-6 py-4 text-xs font-semibold uppercase text-gray-500 ${
                      heading === "Actions" ? "text-right" : "text-left"
                    }`}
                  >
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                    Loading promotions...
                  </td>
                </tr>
              ) : filteredPromotions.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                    No promotions found.
                  </td>
                </tr>
              ) : (
                promotionsPagination.pageItems.map((promotion) => {
                  const coupon = promotion.coupon;
                  const usageCount = Number(coupon?.redemptionCount || 0);

                  return (
                    <tr
                      key={promotion.id}
                      className="border-b border-gray-100 hover:bg-gray-50"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-4">
                          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-50 text-primary-700">
                            <FiGift size={20} />
                          </div>

                          <div>
                            <p className="font-semibold text-gray-900">
                              {promotion.name}
                            </p>

                            <p className="mt-1 max-w-xs truncate text-xs text-gray-500">
                              {promotion.promoBarText || "Promotion campaign"}
                            </p>
                          </div>
                        </div>
                      </td>

                      <td className="px-6 py-4">
                        {coupon ? (
                          <div>
                            <span className="rounded-lg bg-primary-50 px-2.5 py-1 font-mono text-xs font-bold text-primary-800">
                              {coupon.code}
                            </span>
                            <span className="ml-2 text-xs font-semibold text-primary-800">POS + Online</span>

                            <p className="mt-1 text-xs text-gray-500">
                              {usageCount}
                              {coupon.maxRedemptions
                                ? ` / ${coupon.maxRedemptions}`
                                : ""}{" "}
                              used
                            </p>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">No coupon</span>
                        )}
                      </td>

                      <td className="px-6 py-4 text-sm">
                        {coupon ? (
                          <>
                            <p className="font-semibold text-gray-900">
                              {coupon.discountType === "PERCENTAGE"
                                ? `${coupon.discountValue}% OFF`
                                : `₹${coupon.discountValue} OFF`}
                            </p>

                            <p className="mt-1 text-xs text-gray-500">
                              Min. ₹{Number(coupon.minimumOrderAmount || 0)}
                            </p>
                          </>
                        ) : (
                          "—"
                        )}
                      </td>

                      <td className="px-6 py-4 text-sm text-gray-600">
                        <p>{formatDate(promotion.startsAt)}</p>
                        <p className="mt-1 text-xs text-gray-400">
                          to {formatDate(promotion.endsAt)}
                        </p>
                      </td>

                      <td className="px-6 py-4">
                        <span
                          className={`rounded-full px-3 py-1 text-xs font-semibold ${
                            promotion.showInPromoBar
                              ? "bg-blue-100 text-blue-700"
                              : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {promotion.showInPromoBar ? "Visible" : "Hidden"}
                        </span>
                      </td>

                      <td className="px-6 py-4">
                        <span
                          className={`rounded-full px-3 py-1 text-xs font-semibold ${
                            statusStyle[promotion.status]
                          }`}
                        >
                          {promotion.status}
                        </span>
                      </td>

                      <td className="px-6 py-4">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedPromotion(promotion);
                              setShowModal(true);
                            }}
                            className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 hover:bg-primary-50 hover:text-primary-700"
                            title={`Edit ${promotion.name}`}
                          >
                            <FiEdit2 />
                          </button>

                          <button
                            type="button"
                            onClick={() => handleDelete(promotion)}
                            className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-red-500 hover:bg-red-50"
                            title={`Archive ${promotion.name}`}
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <ClientPagination pagination={promotionsPagination} label="promotions" />

        <div className="flex items-center justify-between p-5">
          <p className="text-sm text-gray-500">
            Showing {filteredPromotions.length} campaigns
          </p>
        </div>
      </div>

      <PromotionModal
        open={showModal}
        promotion={selectedPromotion}
        onClose={() => {
          setShowModal(false);
          setSelectedPromotion(null);
        }}
        onSave={handleSave}
      />
    </div>
  );
}

function StatCard({ label, value, valueClass = "" }) {
  return (
    <div className="rounded-3xl border border-gray-100 bg-white p-6">
      <p className="text-sm text-gray-500">{label}</p>
      <h2 className={`mt-2 text-3xl font-bold ${valueClass}`}>{value}</h2>
    </div>
  );
}
